import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './ProductPage.css';
import AddToCartButton from "../components/AddToCartButton/AddToCartButton";
import BuyNowButton from "../components/BuyNowButton/BuyNowButton";
import { PUBLIC_API_URL } from '../services/api.config';

export default function ProductPage({ userData, openCartModal }) {
    const { i18n } = useTranslation();
    const { id } = useParams();

    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState('');

    // Галерея
    const [galleryImages, setGalleryImages] = useState([]);
    const [mainImage, setMainImage] = useState(null);

    // Акордеони (доставка / повернення)
    const [isShippingOpen, setIsShippingOpen] = useState(false);
    const [isReturnsOpen, setIsReturnsOpen] = useState(false);

    // Замість selectedSizeIndex тепер маємо об'єкт із вибраними параметрами
    const [selectedParams, setSelectedParams] = useState({});

    useEffect(() => {
        let isCancelled = false; // Запобігання race conditions

        setLoading(true);
        setErrorMsg('');

        const abortController = new AbortController(); // Для скасування запиту при unmount

        fetch(`${PUBLIC_API_URL}/get-product?productId=${id}`, {
            signal: abortController.signal
        })
            .then((res) => {
                if (!res.ok) {
                    throw new Error(`HTTP error ${res.status}`);
                }
                return res.text(); // Спочатку отримуємо як text для кращої обробки
            })
            .then((text) => {
                if (isCancelled) return; // Не оновлюємо state якщо компонент unmounted

                let data;
                try {
                    data = JSON.parse(text);
                } catch (jsonError) {
                    console.error('JSON parsing error:', jsonError, 'Response text:', text);
                    throw new Error('Invalid response format');
                }

                setProduct(data);

                // Готуємо список усіх зображень
                let allImages = [];
                if (data.mainImage) {
                    allImages.push(data.mainImage);
                }
                if (data.images && data.images.length > 0) {
                    allImages = [...allImages, ...data.images];
                }
                // Прибираємо дублікати
                const uniqueImages = Array.from(new Set(allImages));

                setGalleryImages(uniqueImages);
                setMainImage(uniqueImages[0] || null); // Перше обране як головне

                // Ініціалізуємо вибір параметрів (щоб одразу була ціна)
                // Встановлюємо початкові значення для параметрів ціни
                const initialParams = {};
                
                // Якщо є параметри з типом "price", встановлюємо початкові значення
                if (data.params && Array.isArray(data.params)) {
                    data.params.forEach(param => {
                        if (param.type === 'price' && param.values && param.values.length > 0) {
                            initialParams[param.code] = param.values[0].code;
                        }
                    });
                }
                
                setSelectedParams(initialParams);
            })
            .catch((err) => {
                if (isCancelled || err.name === 'AbortError') return;
                
                console.error('Error fetching product:', err);
                setErrorMsg(err.message || 'Failed to load product');
            })
            .finally(() => {
                if (!isCancelled) {
                    setLoading(false);
                }
            });

        return () => {
            isCancelled = true;
            abortController.abort();
        };
    }, [id]);

    if (loading) {
        return <div className="product-page-container">Loading product...</div>;
    }
    if (errorMsg) {
        return (
            <div className="product-page-container" style={{ color: 'red' }}>
                {errorMsg}
            </div>
        );
    }
    if (!product) {
        return <div className="product-page-container">No product data found</div>;
    }

    const lang = i18n.language === 'ua' ? 'ua' : 'en';

    // Текстові поля
    const productTitle = product.title?.[lang] || 'Untitled';
    const tagline = product.tagline?.[lang] || '';
    const descriptionText = product.description?.[lang] || '';

    // ====================================================
    // 1. Функція пошуку ціни, яка відповідає вибраним параметрам
    // ====================================================
    const getCurrentPrice = () => {
        if (!product.prices || !Array.isArray(product.prices) || product.prices.length === 0) {
            return null;
        }
        
        // Отримуємо параметри з типом "price"
        const priceParams = product.params?.filter(param => param.type === 'price') || [];
        
        if (priceParams.length === 0) {
            // Якщо немає параметрів ціни, повертаємо першу ціну
            return product.prices[0].price;
        }
        
        // Шукаємо серед prices той, у якого збігаються всі обрані параметри
        const matchedPriceItem = product.prices.find(priceItem => {
            // Перевіряємо, чи всі параметри ціни співпадають
            return priceParams.every(param => {
                const paramCode = param.code;
                return priceItem[paramCode] === selectedParams[paramCode];
            });
        });
        
        return matchedPriceItem?.price || null;
    };

    // Поточна ціна (об'єкт { ua, en } або null)
    const currentPrice = getCurrentPrice()?.[lang] || null;

    // Зміна обраного значення параметра
    const handleParamSelect = (paramCode, paramValue) => {
        setSelectedParams(prev => ({
            ...prev,
            [paramCode]: paramValue
        }));
    };

    // Галерея
    const handleThumbClick = (img) => {
        setMainImage(img);
    };

    // ====================================================
    // 2. Рендеримо блоки вибору для параметрів з типом "price"
    // ====================================================
    const renderParamSelectors = () => {
        if (!product.params || !Array.isArray(product.params)) {
            return null;
        }
        
        // Фільтруємо параметри з типом "price"
        const priceParams = product.params.filter(param => param.type === 'price');
        
        if (priceParams.length === 0) {
            return null;
        }
        
        return priceParams.map(param => {
            // Відображаємо параметр, якщо displayType = "block" або не вказаний
            if (param.displayType && param.displayType !== 'block') {
                return null;
            }
            
            return (
                <div className="param-section" key={param.code}>
                    <h3 className="param-title">{param.name[lang]}</h3>
                    <div className="param-options">
                        {param.values.map(value => {
                            const isActive = selectedParams[param.code] === value.code;
                            return (
                                <button
                                    key={value.code}
                                    onClick={() => handleParamSelect(param.code, value.code)}
                                    className={`param-btn ${isActive ? 'active' : ''}`}
                                >
                                    {value.name[lang]}
                                </button>
                            );
                        })}
                    </div>
                </div>
            );
        });
    };

    return (
        <div className="product-page-container">
            {/* LEFT: Вертикальна галерея */}
            <div className="gallery-section">
                <div className="vertical-thumbnails">
                    {galleryImages.map((imgUrl, idx) => (
                        <img
                            key={idx}
                            src={imgUrl}
                            alt={`Thumb-${idx}`}
                            onClick={() => handleThumbClick(imgUrl)}
                            className={`thumb-item ${mainImage === imgUrl ? 'active' : ''}`}
                        />
                    ))}
                </div>

                <div className="main-image">
                    {mainImage ? (
                        <img
                            src={`${mainImage}`}
                            alt="Product"
                        />
                    ) : (
                        <img src="/placeholder.png" alt="Placeholder" />
                    )}
                </div>
            </div>

            {/* RIGHT: Деталі */}
            <div className="product-details">
                <h1 className="product-title">{productTitle}</h1>
                <p className="tagline">{tagline}</p>

                {/* ЦІНА */}
                <p className="product-price">
                    {currentPrice
                        ? (lang === 'ua' ? `${currentPrice} грн` : `$${currentPrice}`)
                        : ''
                    }
                </p>

                {/* Рендеримо вибір параметрів ціни */}
                <div className="product-params">
                    {renderParamSelectors()}
                </div>

                {/* Кнопки CTA -- Buy Now / Add to Cart */}
                <div className="cta-buttons">
                    <AddToCartButton
                        productId={id}
                        priceParams={selectedParams}
                        userData={userData}
                        openCartModal={openCartModal}
                    >
                        {lang === 'ua' ? 'Додати в кошик' : 'Add to Cart'}
                    </AddToCartButton>
                    <BuyNowButton
                        productId={id}
                        priceParams={selectedParams}
                        userData={userData}
                    >
                        {lang === 'ua' ? 'Купити зараз' : 'Buy Now'}
                    </BuyNowButton>
                </div>

                {/* Акордеони для додаткової інформації */}
                <div className="info-accordions">
                    <div className="accordion-item">
                        <h4 onClick={() => setIsShippingOpen(!isShippingOpen)}>
                            {lang === 'ua' ? 'Правила доставки' : 'Shipping Info'}
                        </h4>
                        {isShippingOpen && (
                            <p className="accordion-content">
                                {lang === 'ua'
                                    ? 'Перевірте наявність у магазинах або замовляйте доставку по всій Україні.'
                                    : 'Check availability in stores or order delivery across the country.'}
                            </p>
                        )}
                    </div>

                    <div className="accordion-item">
                        <h4 onClick={() => setIsReturnsOpen(!isReturnsOpen)}>
                            {lang === 'ua' ? 'Правила повернення' : 'Return Policy'}
                        </h4>
                        {isReturnsOpen && (
                            <p className="accordion-content">
                                {lang === 'ua'
                                    ? 'Повернення можливе протягом 14 днів із моменту покупки.'
                                    : 'Returns available within 14 days of purchase.'}
                            </p>
                        )}
                    </div>
                </div>

                <div className="product-description">
                    <h4>{lang === 'ua' ? 'Опис' : 'Description'}</h4>
                    <pre className="desc-text">{descriptionText}</pre>
                </div>
            </div>
        </div>
    );
}
